import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Calendar,
  ChevronLeft,
  DollarSign,
  Flag,
  Globe,
  MapPin,
  Pencil,
  Plane,
  Send,
  ShieldCheck,
  Trash2,
  UserCheck,
  Users
} from 'lucide-react';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import { AvatarDisplay } from '../utils/avatars';
import { useModal } from '../context/ModalContext';
import ReportModal from '../components/common/ReportModal';
import PageHeader from '../components/common/PageHeader';
import { TRAVEL_BUDGET_LABELS, TRAVEL_GENDER_LABELS } from '../utils/travelPostLabels';
import { useIsDesktop } from '../hooks/useIsDesktop';
import LoginPromptModal from '../components/common/LoginPromptModal';

const renderTextWithLinks = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRegex).map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#376ba1', textDecoration: 'underline', wordBreak: 'break-all' }}>
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const formatDate = (dateString) => dateString
  ? new Date(dateString).toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric' })
  : null;

const formatShortDateRange = (start, end) => {
  if (!start) return '日期未定';
  const startLabel = new Date(start).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  if (!end) return startLabel;
  const endLabel = new Date(end).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' });
  return `${startLabel}－${endLabel}`;
};

export default function TravelPostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { showAlert, showConfirm } = useModal();
  const isDesktop = useIsDesktop();
  const [post, setPost] = useState(null);
  const [application, setApplication] = useState(null);
  const [applications, setApplications] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);

  const loadPost = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('travel_posts')
        .select('*, profiles:organizer_id ( id, name, avatar, bio )')
        .eq('id', id)
        .single();

      if (error) throw error;
      setPost(data);

      const { data: applicationRows } = await supabase
        .from('travel_applications')
        .select('id, message, status, created_at, applicant_id, profiles:applicant_id ( id, name, avatar, bio )')
        .eq('post_id', id)
        .order('created_at', { ascending: true });
      setApplications(applicationRows || []);

      if (currentUser && data.organizer_id !== currentUser.id) {
        const { data: ownApplication } = await supabase
          .from('travel_applications')
          .select('*')
          .eq('post_id', id)
          .eq('applicant_id', currentUser.id)
          .maybeSingle();
        setApplication(ownApplication);
      }

      const { data: commentRows } = await supabase
        .from('travel_comments')
        .select('id, user_id, content, created_at, profiles:user_id ( name, avatar )')
        .eq('post_id', id)
        .order('created_at', { ascending: true });
      setComments(commentRows || []);
    } catch (error) {
      console.error('loadPost error:', error);
      showAlert('錯誤', '載入貼文失敗');
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, id, navigate, showAlert]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  const handleApply = async () => {
    if (!currentUser) {
      setIsLoginPromptOpen(true);
      return;
    }
    if (!post) return;
    setIsApplying(true);
    try {
      const { error } = await supabase
        .from('travel_applications')
        .insert({ post_id: post.id, applicant_id: currentUser.id, message: null });

      if (error) {
        if (error.code === '23505') {
          showAlert('提示', '你已經申請過這則旅伴貼文了');
          return;
        }
        throw error;
      }

      showAlert('提示', '已表達興趣！發起人會收到通知。');
      loadPost();
    } catch (error) {
      console.error('handleApply error:', error);
      showAlert('錯誤', '申請失敗，請稍後再試');
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancelApply = async () => {
    if (!application || !currentUser) return;
    setIsApplying(true);
    try {
      const { error } = await supabase
        .from('travel_applications')
        .delete()
        .eq('id', application.id);

      if (error) throw error;
      setApplication(null);
      loadPost();
    } catch (error) {
      console.error('handleCancelApply error:', error);
      showAlert('錯誤', '取消失敗，請稍後再試');
    } finally {
      setIsApplying(false);
    }
  };

  const handleCancelPost = async () => {
    if (!post || !currentUser || post.organizer_id !== currentUser.id) return;
    setIsCancelling(true);
    try {
      const { error } = await supabase
        .from('travel_posts')
        .update({ status: 'cancelled' })
        .eq('id', post.id)
        .eq('organizer_id', currentUser.id);

      if (error) throw error;
      setPost(previous => ({ ...previous, status: 'cancelled' }));
      showAlert('成功', '揪旅伴已取消，歷史紀錄仍會保留');
    } catch (error) {
      console.error('handleCancelPost error:', error);
      showAlert('錯誤', '取消揪旅伴失敗，請稍後再試');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleAddComment = async () => {
    if (post?.status === 'cancelled') return;
    if (!currentUser) {
      setIsLoginPromptOpen(true);
      return;
    }
    const content = newComment.trim();
    if (!content) return;

    try {
      const { data, error } = await supabase
        .from('travel_comments')
        .insert({ post_id: id, user_id: currentUser.id, content })
        .select('id, user_id, content, created_at, profiles:user_id ( name, avatar )')
        .single();

      if (error) throw error;
      setComments(previous => [...previous, data]);
      setNewComment('');
    } catch (error) {
      console.error('handleAddComment error:', error);
      showAlert('錯誤', '留言失敗，請稍後再試');
    }
  };

  const handleEditComment = async (commentId) => {
    const content = editingContent.trim();
    if (!content) return;

    try {
      const { error } = await supabase
        .from('travel_comments')
        .update({ content })
        .eq('id', commentId);

      if (error) throw error;
      setComments(previous => previous.map(comment => comment.id === commentId ? { ...comment, content } : comment));
      setEditingCommentId(null);
      setEditingContent('');
    } catch (error) {
      console.error('handleEditComment error:', error);
      showAlert('錯誤', '編輯失敗，請稍後再試');
    }
  };

  const handleDeleteComment = (commentId) => {
    showConfirm('刪除留言', '確定要刪除這則留言嗎？', async () => {
      try {
        const { error } = await supabase
          .from('travel_comments')
          .delete()
          .eq('id', commentId);

        if (error) throw error;
        setComments(previous => previous.filter(comment => comment.id !== commentId));
      } catch (error) {
        console.error('handleDeleteComment error:', error);
        showAlert('錯誤', '刪除失敗，請稍後再試');
      }
    }, true);
  };

  const formatCommentTime = (isoString) => {
    const date = new Date(isoString);
    const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMinutes < 1) return '剛剛';
    if (diffMinutes < 60) return `${diffMinutes}分鐘前`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} 小時前`;
    return date.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #eef4f9', borderTop: '3px solid #4e8bca', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!post) return null;

  const isOwner = currentUser?.id === post.organizer_id;
  const countries = post.countries?.length > 0 ? post.countries : (post.country ? [post.country] : []);
  const heroImage = post.image_urls?.[0];
  const galleryImages = (post.image_urls || []).slice(1);
  const approvedApplications = applications.filter(item => ['approved', 'accepted'].includes(item.status));
  const remainingSlots = post.max_companions ? Math.max(post.max_companions - approvedApplications.length, 0) : null;

  const summaryItems = [
    { icon: Calendar, label: '行程日期', value: formatShortDateRange(post.trip_start_date, post.trip_end_date) },
    { icon: DollarSign, label: '旅行預算', value: TRAVEL_BUDGET_LABELS[post.budget_range] || '尚未設定' },
    { icon: Users, label: '旅伴偏好', value: `找${post.max_companions || 1}人 · ${TRAVEL_GENDER_LABELS[post.gender_pref] || '性別不限'}` },
    { icon: MapPin, label: '旅行地區', value: countries.join('、') || '尚未設定' }
  ];

  const Section = ({ title, count, children }) => (
    <section style={{ padding: '20px', background: '#ffffff', border: '1px solid rgba(226,232,240,0.86)', borderRadius: '20px', boxShadow: '0 10px 28px rgba(15,23,42,0.055)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
        <span style={{ width: '4px', height: '18px', background: '#4e8bca', borderRadius: '999px' }} />
        <h2 style={{ margin: 0, color: '#0f172a', fontSize: '16px', fontWeight: '850' }}>{title}</h2>
        {count !== undefined && <span style={{ padding: '2px 8px', color: '#64748b', background: '#f1f5f9', borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>{count}</span>}
      </div>
      {children}
    </section>
  );

  const PrimaryAction = ({ fullWidth = false }) => {
    const sharedStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      width: fullWidth ? '100%' : undefined,
      minWidth: fullWidth ? 0 : '142px',
      minHeight: '46px',
      padding: '12px 18px',
      borderRadius: '14px',
      fontFamily: 'inherit',
      fontSize: '13px',
      fontWeight: '850'
    };

    if (post.status === 'cancelled') {
      return <div role="status" style={{ ...sharedStyle, width: fullWidth ? '100%' : undefined, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca' }}>此揪旅伴活動已取消</div>;
    }

    if (isOwner) {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '9px', width: '100%', minWidth: fullWidth ? 0 : '260px' }}>
          <button type="button" onClick={() => navigate(`/travel/edit/${post.id}`)} style={{ ...sharedStyle, minWidth: 0, width: '100%', color: '#ffffff', background: '#4e8bca', border: 'none', boxShadow: '0 8px 20px rgba(78,139,202,0.28)', cursor: 'pointer' }}>
            <Pencil size={16} /> 編輯旅程
          </button>
          <button type="button" onClick={() => showConfirm('取消揪旅伴', '取消後會停止招募並移至歷史紀錄；申請、留言與照片仍會保留。確定要取消嗎？', handleCancelPost, true)} disabled={isCancelling} style={{ ...sharedStyle, minWidth: 0, width: '100%', color: '#ffffff', background: '#dc2626', border: 'none', boxShadow: '0 8px 20px rgba(220,38,38,0.24)', cursor: isCancelling ? 'not-allowed' : 'pointer', opacity: isCancelling ? 0.65 : 1 }}>
            <Trash2 size={16} /> {isCancelling ? '取消中...' : '取消揪旅伴'}
          </button>
        </div>
      );
    }

    if (post.status !== 'open') {
      return <button type="button" disabled style={{ ...sharedStyle, color: '#64748b', background: '#e2e8f0', border: 'none', cursor: 'not-allowed' }}>招募已結束</button>;
    }

    if (application) {
      return (
        <button type="button" onClick={() => showConfirm('取消興趣', '確定要取消表達興趣嗎？', handleCancelApply)} disabled={isApplying} style={{ ...sharedStyle, color: '#376ba1', background: '#eef4f9', border: '1.5px solid #9cc2e6', cursor: isApplying ? 'not-allowed' : 'pointer' }}>
          <UserCheck size={16} /> {isApplying ? '處理中...' : '已表達興趣'}
        </button>
      );
    }

    const isFull = remainingSlots === 0;
    return (
      <button type="button" onClick={handleApply} disabled={isApplying || isFull} style={{ ...sharedStyle, color: '#ffffff', background: isFull ? '#94a3b8' : '#4e8bca', border: 'none', boxShadow: isFull ? 'none' : '0 8px 20px rgba(78,139,202,0.3)', cursor: isApplying || isFull ? 'not-allowed' : 'pointer' }}>
        <Globe size={16} /> {isApplying ? '處理中...' : isFull ? '名額已滿' : '加入這趟旅程'}
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', overflowX: 'clip', background: 'linear-gradient(180deg, #edf4fa 0%, #f6f9fc 34%, #ffffff 100%)', paddingBottom: isDesktop ? '56px' : 'calc(168px + env(safe-area-inset-bottom))' }}>
      <PageHeader
        title="揪旅伴詳情"
        subtitle={post.destination}
        backLabel="返回旅伴列表"
        onBack={() => navigate('/travel')}
        rightAction={!isOwner && currentUser ? (
          <button type="button" aria-label="檢舉貼文" onClick={() => setShowReportModal(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', padding: 0, color: '#64748b', background: '#f8fafc', border: '1px solid #dbe4ee', borderRadius: '50%', cursor: 'pointer' }}>
            <Flag size={18} />
          </button>
        ) : null}
      />
      <div style={{ width: '100%', maxWidth: isDesktop ? '1120px' : '820px', margin: '0 auto', padding: isDesktop ? '24px 24px 0' : 0 }}>
        <div style={{ display: isDesktop ? 'grid' : 'block', gridTemplateColumns: isDesktop ? 'minmax(0, 1fr) 360px' : undefined, gap: isDesktop ? '24px' : 0, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ position: 'relative', minHeight: isDesktop ? '360px' : 'clamp(250px, 54vw, 390px)', overflow: 'hidden', background: 'linear-gradient(145deg, #315f91 0%, #6a9fd3 54%, #b9d7ef 100%)', borderRadius: isDesktop ? '24px' : 0, boxShadow: isDesktop ? '0 14px 36px rgba(15,23,42,0.09)' : 'none' }}>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Plane size={54} color="rgba(255,255,255,0.72)" strokeWidth={1.4} />
              </div>
              {heroImage && <img src={heroImage} alt={post.destination} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(2,8,23,0.18) 0%, transparent 48%, rgba(2,8,23,0.38) 100%)' }} />

            </div>

            <main style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: isDesktop ? '20px' : '14px', marginTop: isDesktop ? '20px' : '-28px', padding: isDesktop ? 0 : '0 16px' }}>
              <section style={{ padding: '22px 20px', background: '#ffffff', border: '1px solid rgba(226,232,240,0.88)', borderRadius: '24px', boxShadow: '0 14px 36px rgba(15,23,42,0.09)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                  {countries.map(country => <span key={country} style={{ padding: '4px 10px', color: '#376ba1', background: '#eef4f9', borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>{country}</span>)}
                  {post.budget_range && <span style={{ padding: '4px 10px', color: '#376ba1', background: '#eef4f9', borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>{TRAVEL_BUDGET_LABELS[post.budget_range] || post.budget_range}</span>}
                  {post.status !== 'open' && <span style={{ padding: '4px 10px', color: post.status === 'cancelled' ? '#991b1b' : '#64748b', background: post.status === 'cancelled' ? '#fef2f2' : '#f1f5f9', borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>{post.status === 'cancelled' ? '已取消' : '已結束'}</span>}
                </div>
                <h2 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: 'clamp(1.45rem, 6vw, 2rem)', fontWeight: '900', letterSpacing: '-0.035em', lineHeight: '1.28' }}>{post.destination}</h2>
                <p style={{ margin: 0, color: '#64748b', fontSize: '13px', lineHeight: '1.55' }}>
                  {post.trip_start_date ? `${formatDate(post.trip_start_date)}${post.trip_end_date ? `－${formatDate(post.trip_end_date)}` : ''}` : '旅行日期尚未確定'}
                </p>

                {!isDesktop && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px', marginTop: '20px' }}>
                  {summaryItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, padding: '12px', background: '#f8fafc', border: '1px solid #edf2f7', borderRadius: '14px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '34px', height: '34px', color: '#4e8bca', background: '#eaf2f8', borderRadius: '11px' }}><Icon size={17} /></span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', color: '#94a3b8', fontSize: '9px', fontWeight: '700' }}>{item.label}</span>
                          <span style={{ display: 'block', marginTop: '2px', overflow: 'hidden', color: '#334155', fontSize: '11px', fontWeight: '800', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>}
              </section>

              <Section title="行程介紹">
                <p style={{ margin: 0, color: '#475569', fontSize: '14px', lineHeight: '1.8', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {post.description ? renderTextWithLinks(post.description) : '發起人尚未補充行程內容。'}
                </p>
              </Section>

              {galleryImages.length > 0 && (
                <Section title="旅程照片" count={`${galleryImages.length + 1}張`}>
                  <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '3px', scrollSnapType: 'x proximity', WebkitOverflowScrolling: 'touch' }}>
                    {galleryImages.map((url, index) => <img key={url} src={url} alt={`旅程照片 ${index + 2}`} loading="lazy" style={{ flex: '0 0 min(78vw, 360px)', width: 'min(78vw, 360px)', height: '190px', objectFit: 'cover', borderRadius: '15px', scrollSnapAlign: 'start' }} />)}
                  </div>
                </Section>
              )}

              {!isDesktop && post.profiles && (
                <Section title="主辦人">
                  <button type="button" onClick={() => navigate(`/travel/user/${post.organizer_id}`)} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: 0, color: 'inherit', background: 'transparent', border: 'none', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
                    <span style={{ flexShrink: 0, width: '52px', height: '52px', overflow: 'hidden', background: '#eef4f9', border: '3px solid #e2edf6', borderRadius: '50%' }}><AvatarDisplay avatarId={post.profiles.avatar} size={52} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1e293b', fontSize: '15px', fontWeight: '850' }}>{post.profiles.name}<ShieldCheck size={15} color="#4e8bca" /></span>
                      <span style={{ display: '-webkit-box', marginTop: '4px', overflow: 'hidden', color: '#64748b', fontSize: '12px', lineHeight: '1.5', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>{post.profiles.bio || '查看旅伴檔案與旅行偏好'}</span>
                    </span>
                    <ChevronLeft size={18} color="#94a3b8" style={{ transform: 'rotate(180deg)' }} />
                  </button>
                </Section>
              )}

              {!isDesktop && applications.length > 0 && (
                <Section title="有興趣的人" count={`${applications.length}人`}>
                  <div style={{ display: 'flex', gap: '14px', overflowX: 'auto', paddingBottom: '2px' }}>
                    {applications.map(item => item.profiles && (
                      <button key={item.id} type="button" onClick={() => navigate(`/travel/user/${item.profiles.id}`)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: '6px', width: '64px', padding: 0, color: '#334155', background: 'transparent', border: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                        <span style={{ width: '50px', height: '50px', overflow: 'hidden', background: '#eef4f9', border: ['approved', 'accepted'].includes(item.status) ? '3px solid #78bd9c' : '3px solid #d8e5f0', borderRadius: '50%' }}><AvatarDisplay avatarId={item.profiles.avatar} size={50} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} /></span>
                        <span style={{ width: '100%', overflow: 'hidden', fontSize: '11px', fontWeight: '750', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.profiles.name}</span>
                      </button>
                    ))}
                  </div>
                </Section>
              )}

              <Section title="討論" count={comments.length}>
                {comments.length === 0 ? (
                  <p style={{ margin: '0 0 16px', padding: '18px 0', color: '#94a3b8', fontSize: '13px', textAlign: 'center' }}>還沒有人留言，快來搶先發表！</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '17px' }}>
                    {comments.map(comment => {
                      const isOwnComment = comment.user_id === currentUser?.id;
                      const isEditing = editingCommentId === comment.id;
                      return (
                        <div key={comment.id} style={{ display: 'flex', flexDirection: isOwnComment ? 'row-reverse' : 'row', gap: '9px' }}>
                          <span style={{ flexShrink: 0, width: '32px', height: '32px', overflow: 'hidden', background: '#eef4f9', borderRadius: '50%', alignSelf: 'flex-end' }}><AvatarDisplay avatarId={comment.profiles?.avatar} size={32} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} /></span>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: isOwnComment ? 'flex-end' : 'flex-start', gap: '4px', maxWidth: '78%' }}>
                            <div style={{ display: 'flex', flexDirection: isOwnComment ? 'row-reverse' : 'row', alignItems: 'center', gap: '6px' }}>
                              <span style={{ color: '#475569', fontSize: '11px', fontWeight: '800' }}>{comment.profiles?.name || '匿名'}</span>
                              <span style={{ color: '#94a3b8', fontSize: '10px' }}>{formatCommentTime(comment.created_at)}</span>
                              {isOwnComment && !isEditing && post.status !== 'cancelled' && (
                                <span style={{ display: 'flex', gap: '2px' }}>
                                  <button type="button" aria-label="編輯留言" onClick={() => { setEditingCommentId(comment.id); setEditingContent(comment.content); }} style={{ display: 'flex', padding: '2px', color: '#94a3b8', background: 'transparent', border: 'none', cursor: 'pointer' }}><Pencil size={12} /></button>
                                  <button type="button" aria-label="刪除留言" onClick={() => handleDeleteComment(comment.id)} style={{ display: 'flex', padding: '2px', color: '#f59a9a', background: 'transparent', border: 'none', cursor: 'pointer' }}><Trash2 size={12} /></button>
                                </span>
                              )}
                            </div>
                            {isEditing ? (
                              <div style={{ width: '100%' }}>
                                <textarea value={editingContent} onChange={event => setEditingContent(event.target.value)} rows={2} style={{ boxSizing: 'border-box', width: '100%', padding: '8px 11px', border: '1.5px solid #4e8bca', borderRadius: '12px', outline: 'none', resize: 'none', fontFamily: 'inherit', fontSize: '13px' }} />
                                <div style={{ display: 'flex', gap: '6px', marginTop: '5px' }}>
                                  <button type="button" onClick={() => { setEditingCommentId(null); setEditingContent(''); }} style={{ flex: 1, padding: '6px', color: '#64748b', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '999px', fontSize: '12px', cursor: 'pointer' }}>取消</button>
                                  <button type="button" onClick={() => handleEditComment(comment.id)} style={{ flex: 1, padding: '6px', color: '#ffffff', background: '#4e8bca', border: 'none', borderRadius: '999px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>儲存</button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ padding: '9px 13px', color: isOwnComment ? '#ffffff' : '#1e293b', background: isOwnComment ? '#4e8bca' : '#f1f5f9', borderRadius: isOwnComment ? '15px 4px 15px 15px' : '4px 15px 15px 15px', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLinks(comment.content)}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: comments.length > 0 ? '13px' : 0, borderTop: comments.length > 0 ? '1px solid #f1f5f9' : 'none' }}>
                  <input type="text" value={newComment} disabled={post.status === 'cancelled'} onChange={event => setNewComment(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); handleAddComment(); } }} placeholder={post.status === 'cancelled' ? '揪旅伴已取消，討論已關閉' : '留下你的想法...'} style={{ flex: 1, minWidth: 0, padding: '11px 15px', color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '999px', outline: 'none', fontFamily: 'inherit', fontSize: '13px' }} />
                  <button type="button" aria-label="送出留言" onClick={handleAddComment} disabled={post.status === 'cancelled' || !newComment.trim()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '42px', height: '42px', padding: 0, color: '#ffffff', background: post.status !== 'cancelled' && newComment.trim() ? '#4e8bca' : '#dbe4ee', border: 'none', borderRadius: '50%', cursor: post.status !== 'cancelled' && newComment.trim() ? 'pointer' : 'default' }}><Send size={16} /></button>
                </div>
              </Section>
            </main>
          </div>

          {isDesktop && (
            <aside data-testid="travel-desktop-sidebar" style={{ position: 'sticky', top: '96px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <section aria-label="旅程摘要與加入操作" style={{ padding: '22px', background: '#ffffff', border: '1px solid rgba(226,232,240,0.9)', borderRadius: '22px', boxShadow: '0 10px 30px rgba(15,23,42,0.07)' }}>
                <div style={{ marginBottom: '18px' }}>
                  <span style={{ display: 'block', color: post.status === 'cancelled' ? '#b91c1c' : '#94a3b8', fontSize: '11px', fontWeight: '700' }}>{post.status === 'cancelled' ? '揪旅伴已取消' : remainingSlots === null ? '旅伴招募中' : `還有${remainingSlots}個名額`}</span>
                  <h2 style={{ margin: '4px 0 0', color: '#0f172a', fontSize: '20px', fontWeight: '850', lineHeight: '1.35' }}>{post.destination}</h2>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
                  {summaryItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0, padding: '11px 12px', background: '#f8fafc', border: '1px solid #edf2f7', borderRadius: '13px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '36px', height: '36px', color: '#4e8bca', background: '#eaf2f8', borderRadius: '11px' }}><Icon size={18} /></span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', fontWeight: '700' }}>{item.label}</span>
                          <span style={{ display: 'block', marginTop: '2px', color: '#334155', fontSize: '13px', fontWeight: '800', lineHeight: '1.4' }}>{item.value}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>

                {post.profiles && (
                  <button type="button" onClick={() => navigate(`/travel/user/${post.organizer_id}`)} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', minHeight: '56px', marginBottom: '16px', padding: '9px 10px', color: 'inherit', background: '#f6f9fc', border: '1px solid #e2e8f0', borderRadius: '14px', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
                    <span style={{ flexShrink: 0, width: '40px', height: '40px', overflow: 'hidden', background: '#eef4f9', border: '2px solid #d7e7f4', borderRadius: '50%' }}><AvatarDisplay avatarId={post.profiles.avatar} size={40} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1e293b', fontSize: '13px', fontWeight: '850' }}>{post.profiles.name}<ShieldCheck size={14} color="#4e8bca" /></span>
                      <span style={{ display: 'block', marginTop: '2px', color: '#64748b', fontSize: '11px' }}>主辦人 · 查看旅伴檔案</span>
                    </span>
                    <ChevronLeft size={17} color="#94a3b8" style={{ transform: 'rotate(180deg)' }} />
                  </button>
                )}

                {applications.length > 0 && (
                  <div style={{ marginBottom: '18px', paddingTop: '14px', borderTop: '1px solid #eef2f6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '9px' }}>
                      <span style={{ color: '#475569', fontSize: '12px', fontWeight: '800' }}>有興趣的人</span>
                      <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '700' }}>{applications.length}人</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', minHeight: '38px' }}>
                      {applications.slice(0, 6).map((item, index) => item.profiles && (
                        <button key={item.id} type="button" aria-label={`查看 ${item.profiles.name} 的旅伴檔案`} onClick={() => navigate(`/travel/user/${item.profiles.id}`)} style={{ display: 'flex', width: '38px', height: '38px', marginLeft: index === 0 ? 0 : '-9px', padding: 0, overflow: 'hidden', background: '#eef4f9', border: ['approved', 'accepted'].includes(item.status) ? '2px solid #4e8bca' : '2px solid #ffffff', borderRadius: '50%', cursor: 'pointer' }}>
                          <AvatarDisplay avatarId={item.profiles.avatar} size={38} style={{ width: '100%', height: '100%', border: 'none', boxShadow: 'none' }} />
                        </button>
                      ))}
                      {applications.length > 6 && <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', marginLeft: '-9px', color: '#475569', background: '#e2e8f0', border: '2px solid #ffffff', borderRadius: '50%', fontSize: '10px', fontWeight: '800' }}>+{applications.length - 6}</span>}
                    </div>
                  </div>
                )}

                <PrimaryAction fullWidth />
              </section>
            </aside>
          )}
        </div>
      </div>

      {!isDesktop && <div data-testid="travel-mobile-action-bar" style={{ position: 'fixed', left: 0, right: 0, bottom: '70px', zIndex: 90, padding: '10px 16px calc(10px + env(safe-area-inset-bottom))', background: 'rgba(255,255,255,0.95)', borderTop: '1px solid rgba(226,232,240,0.92)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', maxWidth: '788px', margin: '0 auto' }}>
          {!isOwner && <div style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', color: '#94a3b8', fontSize: '10px', fontWeight: '700' }}>{remainingSlots === null ? '旅伴招募中' : `還有${remainingSlots}個名額`}</span>
            <span style={{ display: 'block', marginTop: '1px', overflow: 'hidden', color: '#334155', fontSize: '13px', fontWeight: '850', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.destination}</span>
          </div>}
          <PrimaryAction fullWidth={isOwner} />
        </div>
      </div>}

      {showReportModal && post.profiles && (
        <ReportModal targetUser={{ id: post.organizer_id, profile: post.profiles }} reporterId={currentUser?.id} travelPostId={post.id} onClose={() => setShowReportModal(false)} />
      )}

      <LoginPromptModal isOpen={isLoginPromptOpen} onClose={() => setIsLoginPromptOpen(false)} />
    </div>
  );
}
